import React, { useState, useEffect } from "react";
import axios from "axios";
import { Row, Col, Card, Button, Modal, Input, Select } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons"; // Removed SearchOutlined icon
import "antd/dist/antd.css"; // Ensure Ant Design styles are imported
import ResponsiveTable from "../components/ResponsiveTable";
import LoadingSpinner from "../components/LoadingSpinner";
const { Option } = Select;

const LogManagement = () => {
  const [data, setData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newRecord, setNewRecord] = useState({
    projectId: "",
    errorDescription: "",
    createdBy: "",
    environment: "dev",
    status: "open",
    notes: "",
  });
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // >>>>>>> ADDED: state for project name mapping
  const [projectMap, setProjectMap] = useState({});
  // <<<<<<<

  // Fetch logs from the API
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await fetch("https://api.essarsystems.com/vault_backend/api/logs/");
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to fetch logs:", errorText);
          throw new Error("Failed to fetch logs");
        }

        const data = await response.json();

        data.sort((a, b) =>
          b.error_id.localeCompare(a.error_id)
        );

        setData(
          data.map((log) => ({
            key: log.record_id,
            recordId: log.record_id,
            projectId: log.project_id,
            errorId: log.error_id,
            errorDescription: log.error_description,
            createdDate: log.created_date,
            createdBy: log.created_by,
            lastUpdatedBy: log.last_updated_by,
            environment: log.environment,
            status: log.status,
            notes: log.notes,
          }))
        );
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setIsInitialLoad(false); // Add this finally block!
      }
    };

    fetchLogs(); // Initial fetch

    const handleFocus = () => {
      fetchLogs(); // Refetch logs when the page is focused
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await fetch("https://api.essarsystems.com/vault_backend/api/logs/");
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to fetch logs:", errorText);
          throw new Error("Failed to fetch logs");
        }

        const data = await response.json();

        data.sort((a, b) =>
          b.error_id.localeCompare(a.error_id)
        );

        setData(
          data.map((log) => ({
            key: log.record_id,
            recordId: log.record_id,
            projectId: log.project_id,
            errorId: log.error_id,
            errorDescription: log.error_description,
            createdDate: log.created_date,
            createdBy: log.created_by,
            lastUpdatedBy: log.last_updated_by,
            environment: log.environment,
            status: log.status,
            notes: log.notes,
          }))
        );
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setIsInitialLoad(false); // Add this finally block!
      }
    };

    fetchLogs();

    const interval = setInterval(fetchLogs, 30000); // Auto-refresh every 30 seconds
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  // >>>>>>> ADDED: fetch project list and build ID → name mapping
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch("https://api.essarsystems.com/vault_backend/projects/");
        if (!res.ok) throw new Error("Failed to fetch projects");
        const projects = await res.json();
        const map = {};
        projects.forEach(p => {
          // Assuming response has { project_id, project_name }
          map[p.project_id] = p.project_name;
        });
        setProjectMap(map);
      } catch (err) {
        console.warn("Could not load project names from API, using static fallback", err);
        // Static fallback in case API is unreachable
        setProjectMap({
          // "Pro0000003": "ASVASA",
          // "Pro0000002": "Essar Events",
          // "Pro0000001": "Global Tiffin",
        });
      }
    };
    fetchProjects();
  }, []);
  // <<<<<<<

  // Filter data based on search text
  useEffect(() => {
    setFilteredData(
      data.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      )
    );
  }, [searchText, data]);

  // Add a new log
  const addNewRecord = async () => {
    try {
      const requestBody = {
        project_id: newRecord.projectId,
        error_description: newRecord.errorDescription,
        created_by: newRecord.createdBy,
        last_updated_by: newRecord.lastUpdatedBy,
        environment: newRecord.environment,
        status: newRecord.status,
        notes: newRecord.notes,
      };

      const response = await fetch("https://api.essarsystems.com/vault_backend/api/logs/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to create log:", errorText);
        throw new Error("Failed to create log");
      }

      const data = await response.json();

      // Ensure the key is set correctly
      setData((prevData) => [
        ...prevData,
        {
          key: data.record_id, // Use record_id as the key
          recordId: data.record_id,
          projectId: data.project_id,
          errorId: data.error_id,
          errorDescription: data.error_description,
          createdDate: data.created_date,
          createdBy: data.created_by,
          lastUpdatedBy: data.last_updated_by,
          environment: data.environment,
          status: data.status,
          notes: data.notes,
        },
      ]);

      setIsAdding(false);
      setNewRecord({
        projectId: "",
        errorDescription: "",
        createdBy: "",
        lastUpdatedBy: "",
        environment: "dev",
        status: "open",
        notes: "",
      });
    } catch (error) {
      console.error("Error adding log:", error);
    }
  };

  // Update an existing log
  const saveEdit = async () => {
    try {
      const requestBody = {
        status: editingRecord.status,
        notes: editingRecord.notes,
        last_updated_by: editingRecord.lastUpdatedBy, // Include last_updated_by in the request body
      };

      const response = await fetch(`https://api.essarsystems.com/vault_backend/api/logs/${editingRecord.recordId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update log:", errorText);
        throw new Error("Failed to update log");
      }

      const updatedLog = await response.json();

      // Update the data state with the updated log
      setData((prevData) =>
        prevData.map((item) =>
          item.recordId === editingRecord.recordId
            ? {
              ...item,
              status: updatedLog.status,
              notes: updatedLog.notes,
              lastUpdatedBy: updatedLog.last_updated_by, // Update last_updated_by in the state
            }
            : item
        )
      );

      // Reset editing state
      setIsEditing(false);
      setEditingRecord(null);
    } catch (error) {
      console.error("Error updating log:", error);
      alert("An error occurred while updating the log. Please try again."); // Show an alert to the user
    }
  };

  // Delete a log
  const deleteRow = async (key) => {
    try {
      const response = await fetch(`https://api.essarsystems.com/vault_backend/api/logs/${key}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        console.error("Failed to delete log:", await response.text());
        throw new Error("Failed to delete log");
      }

      const result = await response.json();
      console.log(result.message); // Log the success message

      setData((prevData) => prevData.filter((item) => item.key !== key));
    } catch (error) {
      console.error("Error deleting log:", error);
    }
  };

  const editRow = (record) => {
    setIsEditing(true);
    setEditingRecord({ ...record });
  };

  const toggleStatus = async (key) => {
    const record = data.find((item) => item.key === key);
    if (record) {
      try {
        const updatedStatus = record.status === "open" ? "closed" : "open";

        const response = await fetch(`https://api.essarsystems.com/vault_backend/api/logs/${record.recordId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: updatedStatus }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to update status:", errorText);
          throw new Error("Failed to update status");
        }

        const updatedLog = await response.json();

        // Update the data state with the new status
        setData((prevData) =>
          prevData.map((item) =>
            item.key === record.key
              ? { ...item, status: updatedLog.status }
              : item
          )
        );
      } catch (error) {
        console.error("Error toggling status:", error);
      }
    } else {
      console.error("Record not found for key:", key);
    }
  };

  const columns = [
    {
      title: "RECORD ID",
      dataIndex: "recordId",
      key: "recordId",
      sorter: (a, b) => a.recordId.localeCompare(b.recordId), // Sorting by string comparison
    },
    // >>>>>>> MODIFIED: changed title to PROJECT NAME, added render & updated sorter
    {
      title: "PROJECT NAME",
      dataIndex: "projectId",
      key: "projectId",
      render: (text) => projectMap[text] || text,  // show name if available, else ID
      sorter: (a, b) => {
        const nameA = projectMap[a.projectId] || a.projectId;
        const nameB = projectMap[b.projectId] || b.projectId;
        return nameA.localeCompare(nameB);
      },
    },
    // <<<<<<<
    {
      title: "CREATED DATE",
      dataIndex: "createdDate",
      key: "createdDate",
      sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate), // Sorting by date
    },
    {
      title: "ERROR ID",
      dataIndex: "errorId",
      key: "errorId",
      sorter: (a, b) => a.errorId.localeCompare(b.errorId), // Sorting by string comparison
    },
    {
      title: "ERROR DESCRIPTION",
      dataIndex: "errorDescription",
      key: "errorDescription",
      render: (text) => {
        if (!text) return "";

        const shortText =
          text.length > 50 ? text.substring(0, 50) + "..." : text;

        return (
          <span
            style={{ cursor: "pointer", color: "#1890ff" }}
            onClick={() => {
              Modal.info({
                title: "Full Error Description",
                content: text,
                width: 600,
              });
            }}
          >
            {shortText}
          </span>
        );
      },
    },
    {
      title: "CREATED BY",
      dataIndex: "createdBy",
      key: "createdBy",
    },
    {
      title: "ENVIRONMENT",
      dataIndex: "environment",
      key: "environment",
    },
    {
      title: "LAST UPDATED BY",
      dataIndex: "lastUpdatedBy",
      key: "lastUpdatedBy",
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Button
          type="primary"
          style={{
            backgroundColor: status === "open" ? "#1890ff" : "#d9d9d9", // Blue for "open", default for others
            borderColor: status === "open" ? "#1890ff" : "#d9d9d9",
            color: status === "open" ? "#fff" : "#000",
          }}
          onClick={() => toggleStatus(record.key)}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)} {/* Capitalize status */}
        </Button>
      ),
    },
    {
      title: "NOTES",
      dataIndex: "notes",
      key: "notes",
      render: (text) => {
        if (!text) return "";

        const shortText =
          text.length > 50 ? text.substring(0, 50) + "..." : text;

        return (
          <span
            style={{ cursor: "pointer", color: "#1890ff" }}
            onClick={() => {
              Modal.info({
                title: "Full Notes",
                content: text,
                width: 600,
              });
            }}
          >
            {shortText}
          </span>
        );
      },
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => editRow(record)}
            style={{ marginRight: 8, color: "#1890ff", borderColor: "#1890ff" }}
          >
            Edit
          </Button>
          <Button onClick={() => deleteRow(record.key)} danger>
            ❌ Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="log-management">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title="Log Management Table" // Added title
            extra={
              <>
                <Input
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200, marginRight: 8 }}
                />
                {/* Removed SearchOutlined icon */}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAdding(true)}
                >
                  Add Log
                </Button>
              </>
            }
          >{/* Add this condition: */}
            {isInitialLoad ? (
              <LoadingSpinner text="Loading records..." />
            ) : (
              <div className="table-responsive">
                <ResponsiveTable
                  columns={columns}
                  dataSource={filteredData}
                  rowKey="recordId"
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1200 }}
                  className="ant-border-space"
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        title="Edit Log"
        visible={isEditing}
        onOk={saveEdit}
        onCancel={() => {
          setIsEditing(false);
          setEditingRecord(null);
        }}
      >
        <Input
          placeholder="Error Description"
          value={editingRecord?.errorDescription}
          onChange={(e) =>
            setEditingRecord({
              ...editingRecord,
              errorDescription: e.target.value,
            })
          }
          style={{ marginBottom: 8 }}
        />
        <Select
          placeholder="Status"
          value={editingRecord?.status}
          onChange={(value) =>
            setEditingRecord({ ...editingRecord, status: value })
          }
          style={{ width: "100%", marginBottom: 8 }}
        >
          <Option value="open">Open</Option>
          <Option value="closed">Closed</Option>
        </Select>
        <Input
          placeholder="Last Updated By"
          value={editingRecord?.lastUpdatedBy}
          onChange={(e) =>
            setEditingRecord({
              ...editingRecord,
              lastUpdatedBy: e.target.value,
            })
          }
          style={{ marginBottom: 8 }}
        />
        <Input.TextArea
          placeholder="Notes"
          value={editingRecord?.notes}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, notes: e.target.value })
          }
          rows={4}
          style={{ marginBottom: 8 }}
        />
      </Modal>

      {/* Add Modal */}
      <Modal
        title="Add New Log"
        visible={isAdding}
        onOk={addNewRecord}
        onCancel={() => setIsAdding(false)}
      >
        <Input
          placeholder="Project ID"
          value={newRecord.projectId}
          onChange={(e) =>
            setNewRecord({ ...newRecord, projectId: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Error Description"
          value={newRecord.errorDescription}
          onChange={(e) =>
            setNewRecord({ ...newRecord, errorDescription: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Created By"
          value={newRecord.createdBy}
          onChange={(e) =>
            setNewRecord({ ...newRecord, createdBy: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="Last Updated By"
          value={newRecord.lastUpdatedBy}
          onChange={(e) =>
            setNewRecord({ ...newRecord, lastUpdatedBy: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        <Select
          placeholder="Environment"
          value={newRecord.environment}
          onChange={(value) =>
            setNewRecord({ ...newRecord, environment: value })
          }
          style={{ width: "100%", marginBottom: 8 }}
        >
          <Option value="dev">Dev</Option>
          <Option value="test">Test</Option>
          <Option value="production">Production</Option>
        </Select>
        <Select
          placeholder="Status"
          value={newRecord.status}
          onChange={(value) =>
            setNewRecord({ ...newRecord, status: value })
          }
          style={{ width: "100%", marginBottom: 8 }}
        >
          <Option value="open">Open</Option>
          <Option value="closed">Closed</Option>
        </Select>
        <Input.TextArea
          placeholder="Notes"
          value={newRecord.notes}
          onChange={(e) =>
            setNewRecord({ ...newRecord, notes: e.target.value })
          }
          rows={4}
          style={{ marginBottom: 8 }}
        />
      </Modal>
    </div>
  );
};

export default LogManagement;